'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Loader2, Save, AlertCircle } from 'lucide-react';
import VerifiedBadge from '@/components/ui/VerifiedBadge';

interface ProfileFormData {
  businessName: string;
  tagline: string;
  description: string;
  category: string;
  email: string;
  phone: string;
  website: string;
  facebook: string;
  twitter: string;
  instagram: string;
  yearEstablished: string;
  employeeCount: string;
  registrationNumber: string;
}

const categories = [
  'Trades & Construction', 'Restaurants & Cafés', 'Health & Medical',
  'Legal Services', 'Accountants & Finance', 'Estate Agents & Property',
  'Automotive', 'Hair & Beauty', 'Garden & Landscaping', 'Retail & Shopping',
  'IT & Technology', 'Education & Tutoring',
];

export default function ProfileTab() {
  const [isLoading, setIsLoading] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ProfileFormData>({
    defaultValues: {
      businessName: 'Sutton Builders Ltd',
      tagline: 'West Suffolk\'s trusted builders since 1987',
      description: 'Sutton Builders Ltd has been serving West Suffolk homeowners and businesses since 1987. A family-run company with over 35 years of experience, we pride ourselves on quality craftsmanship, transparent pricing and a personal approach to every project.',
      category: 'Trades & Construction',
      email: 'info@suttonbuilders.co.uk',
      phone: '01284 756 234',
      website: 'https://suttonbuilders.co.uk',
      facebook: 'suttonbuilders',
      twitter: '',
      instagram: 'suttonbuilders_suffolk',
      yearEstablished: '1987',
      employeeCount: '11-50',
      registrationNumber: '03847291',
    },
  });

  const onSubmit = async (data: ProfileFormData) => {
    setIsLoading(true);
    // Backend integration point: PUT /api/businesses/{id}/profile
    await new Promise((r) => setTimeout(r, 1000));
    toast.success('Profile saved successfully');
    setIsDirty(false);
    setIsLoading(false);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} onChange={() => setIsDirty(true)}>
      {/* Verification status */}
      <div className="card-surface p-4 mb-6 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <VerifiedBadge size="md" />
          <div>
            <p className="text-sm font-semibold text-foreground">Council Verification Active</p>
            <p className="text-xs text-muted-foreground">Last verified: 12 March 2026 by West Suffolk Council</p>
          </div>
        </div>
        <span className="text-xs text-positive font-semibold bg-positive/10 px-2.5 py-1 rounded">
          Enhanced Tier
        </span>
      </div>

      {/* Unsaved changes warning */}
      {isDirty && (
        <div className="flex items-center gap-2 bg-warning/10 border border-warning/30 rounded px-4 py-3 mb-6">
          <AlertCircle size={15} className="text-warning shrink-0" />
          <p className="text-sm text-warning font-semibold">You have unsaved changes</p>
        </div>
      )}

      {/* Business identity */}
      <div className="card-surface p-6 mb-5">
        <h3 className="font-bold text-base text-foreground mb-5 pb-3 border-b border-border">
          Business Identity
        </h3>
        <div className="space-y-4">
          <div>
            <label htmlFor="mgmt-bizName" className="block text-sm font-semibold text-foreground mb-1.5">
              Business name <span className="text-negative">*</span>
            </label>
            <input
              id="mgmt-bizName"
              type="text"
              className={`input-field ${errors.businessName ? 'border-negative' : ''}`}
              {...register('businessName', { required: 'Business name is required' })}
            />
            {errors.businessName && <p className="text-negative text-xs mt-1">{errors.businessName.message}</p>}
          </div>

          <div>
            <label htmlFor="mgmt-tagline" className="block text-sm font-semibold text-foreground mb-1.5">
              Tagline
            </label>
            <p className="text-xs text-muted-foreground mb-1.5">A short, memorable description shown in search results</p>
            <input
              id="mgmt-tagline"
              type="text"
              maxLength={100}
              className="input-field"
              {...register('tagline')}
            />
          </div>

          <div>
            <label htmlFor="mgmt-desc" className="block text-sm font-semibold text-foreground mb-1.5">
              Business description <span className="text-negative">*</span>
            </label>
            <p className="text-xs text-muted-foreground mb-1.5">Describe your business, services and what makes you stand out. Min 50 characters.</p>
            <textarea
              id="mgmt-desc"
              rows={5}
              className={`input-field resize-none ${errors.description ? 'border-negative' : ''}`}
              {...register('description', {
                required: 'Description is required',
                minLength: { value: 50, message: 'Must be at least 50 characters' },
              })}
            />
            {errors.description && <p className="text-negative text-xs mt-1">{errors.description.message}</p>}
          </div>

          <div>
            <label htmlFor="mgmt-cat" className="block text-sm font-semibold text-foreground mb-1.5">
              Primary category <span className="text-negative">*</span>
            </label>
            <select
              id="mgmt-cat"
              className="input-field"
              {...register('category', { required: 'Category is required' })}
            >
              {categories.map((cat) => (
                <option key={`mgmt-cat-${cat}`} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="mgmt-year" className="block text-sm font-semibold text-foreground mb-1.5">
                Year established
              </label>
              <input
                id="mgmt-year"
                type="number"
                min="1800"
                max="2026"
                className="input-field"
                {...register('yearEstablished')}
              />
            </div>
            <div>
              <label htmlFor="mgmt-emp" className="block text-sm font-semibold text-foreground mb-1.5">
                Number of employees
              </label>
              <select id="mgmt-emp" className="input-field" {...register('employeeCount')}>
                <option value="1">Just me (sole trader)</option>
                <option value="2-10">2–10</option>
                <option value="11-50">11–50</option>
                <option value="51-200">51–200</option>
                <option value="200+">200+</option>
              </select>
            </div>
          </div>

          <div>
            <label htmlFor="mgmt-regnum" className="block text-sm font-semibold text-foreground mb-1.5">
              Companies House registration number
            </label>
            <p className="text-xs text-muted-foreground mb-1.5">Required for council verification on Enhanced and Featured tiers</p>
            <input
              id="mgmt-regnum"
              type="text"
              className="input-field font-mono"
              placeholder="e.g. 12345678"
              {...register('registrationNumber')}
            />
          </div>
        </div>
      </div>

      {/* Contact details */}
      <div className="card-surface p-6 mb-5">
        <h3 className="font-bold text-base text-foreground mb-5 pb-3 border-b border-border">
          Contact Details
        </h3>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="mgmt-email" className="block text-sm font-semibold text-foreground mb-1.5">
                Business email <span className="text-negative">*</span>
              </label>
              <input
                id="mgmt-email"
                type="email"
                className={`input-field ${errors.email ? 'border-negative' : ''}`}
                {...register('email', {
                  required: 'Email is required',
                  pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
                })}
              />
              {errors.email && <p className="text-negative text-xs mt-1">{errors.email.message}</p>}
            </div>
            <div>
              <label htmlFor="mgmt-phone" className="block text-sm font-semibold text-foreground mb-1.5">
                Business phone <span className="text-negative">*</span>
              </label>
              <input
                id="mgmt-phone"
                type="tel"
                className={`input-field ${errors.phone ? 'border-negative' : ''}`}
                {...register('phone', { required: 'Phone number is required' })}
              />
              {errors.phone && <p className="text-negative text-xs mt-1">{errors.phone.message}</p>}
            </div>
          </div>
          <div>
            <label htmlFor="mgmt-website" className="block text-sm font-semibold text-foreground mb-1.5">
              Website URL
            </label>
            <input
              id="mgmt-website"
              type="url"
              className="input-field"
              placeholder="https://yourbusiness.co.uk"
              {...register('website')}
            />
          </div>
        </div>
      </div>

      {/* Social media */}
      <div className="card-surface p-6 mb-6">
        <h3 className="font-bold text-base text-foreground mb-5 pb-3 border-b border-border">
          Social Media
        </h3>
        <div className="space-y-4">
          {[
            { id: 'mgmt-fb', name: 'facebook' as const, label: 'Facebook', placeholder: 'yourbusiness', prefix: 'facebook.com/' },
            { id: 'mgmt-tw', name: 'twitter' as const, label: 'Twitter / X', placeholder: 'yourbusiness', prefix: 'x.com/' },
            { id: 'mgmt-ig', name: 'instagram' as const, label: 'Instagram', placeholder: 'yourbusiness', prefix: 'instagram.com/' },
          ].map((social) => (
            <div key={social.id}>
              <label htmlFor={social.id} className="block text-sm font-semibold text-foreground mb-1.5">
                {social.label}
              </label>
              <div className="flex">
                <span className="flex items-center px-3 bg-muted border border-r-0 border-border rounded-l text-xs text-muted-foreground whitespace-nowrap">
                  {social.prefix}
                </span>
                <input
                  id={social.id}
                  type="text"
                  className="input-field rounded-l-none"
                  placeholder={social.placeholder}
                  {...register(social.name)}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Sticky save bar */}
      <div className="sticky bottom-0 bg-card border-t border-border px-6 py-4 -mx-6 flex items-center justify-between gap-4">
        <p className="text-xs text-muted-foreground">
          Changes are reviewed by West Suffolk Council before going live on the directory.
        </p>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => setIsDirty(false)}
            className="btn-secondary text-sm"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="btn-primary text-sm"
            style={{ minWidth: 140 }}
          >
            {isLoading ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save size={14} />
                Save Profile
              </>
            )}
          </button>
        </div>
      </div>
    </form>
  );
}